import axios from "axios";

/**
 * Lightweight site-analytics tracker.
 *
 * All calls are fire-and-forget: errors are swallowed silently so tracking
 * never interrupts the user experience.
 *
 * Events:
 *   page_view       – fired automatically on every route change
 *   register_click  – user clicked the Register button for a registration
 *   category_click  – user clicked a category tile on the grid homepage
 *   info_click      – user clicked an Info / PDF button
 */

// -------------------------------------------------------------------
// Session ID — anonymous per-browser-tab UUID, stored in sessionStorage
// -------------------------------------------------------------------
const SESSION_KEY = "bc_session_id";

function getSessionId() {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    // Simple UUID v4 without external deps
    id = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

// -------------------------------------------------------------------
// API endpoint — resolve from the same base the app already uses
// -------------------------------------------------------------------
function getStatsEndpoint() {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isProd =
    !host.includes("localhost") &&
    !host.includes("demo.beclutch.club") &&
    !host.includes("192.168.");
  const base = isProd
    ? "https://mobile.beclutch.club/api/"
    : "https://dev.beclutch.club/api/";
  return base + "stats/track";
}

// -------------------------------------------------------------------
// Core send function
// -------------------------------------------------------------------
function send(eventType, extras = {}) {
  try {
    const domain = window.location.hostname;
    const page = window.location.pathname + window.location.search;

    const params = new URLSearchParams();
    params.append("domain", domain);
    params.append("event_type", eventType);
    params.append("page", page);
    params.append("sid", getSessionId());
    params.append("referrer", document.referrer || "");
    params.append("screen_width", String(window.innerWidth || ""));

    if (extras.registration_id != null)
      params.append("registration_id", String(extras.registration_id));
    if (extras.category_id != null)
      params.append("category_id", String(extras.category_id));

    axios
      .post(getStatsEndpoint(), params, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        timeout: 5000,
      })
      .catch(() => {
        // Silently swallow — analytics must never surface errors to the user
      });
  } catch {
    // Silently swallow
  }
}

// -------------------------------------------------------------------
// Public API
// -------------------------------------------------------------------

/** Record a page view. Called automatically by the RouteTracker component. */
export const trackPageView = () => send("page_view");

/** Record a click on a Register button.
 * @param {number|string} registrationId
 */
export const trackRegisterClick = (registrationId) =>
  send("register_click", { registration_id: registrationId });

/** Record a click on a category tile.
 * @param {number|string} categoryId
 */
export const trackCategoryClick = (categoryId) =>
  send("category_click", { category_id: categoryId });

/** Record a click on an Info / PDF link.
 * @param {number|string} [registrationId]
 * @param {number|string} [categoryId]
 */
export const trackInfoClick = (registrationId, categoryId) =>
  send("info_click", { registration_id: registrationId, category_id: categoryId });
